/**
 * Ingredient and batch costing.
 *
 * Unit cost lives on the lot — currency per stored unit (grams, or packs for
 * yeast). A live estimate runs the same FIFO allocator as availability; a
 * batch freezes the deduction's lines so repricing a sack cannot move last
 * week's brew. Changing workspace currency does not convert numbers.
 */

import type { BatchCostLine, BatchCostSnapshot } from '../document/batch';
import type { CatalogRef } from '../document/common';
import type { InventoryKind, InventoryUnit } from '../document/inventory';
import { roundQuantity } from '../document/inventory';
import type { RecipeDoc } from '../document/recipe';
import {
  allocateRequirements,
  type CustomIngredientTable,
  type IngredientIdentity,
  type InventoryTable,
  lotMatches,
  recipeRequirements,
  type ShoppingList,
} from './availability';
import type { InventoryDeductionPlan } from './deduct';

/** Decimal places a displayed total is rounded to (currency minor units). */
export const MONEY_DECIMALS = 2;

const MONEY_FACTOR = 10 ** MONEY_DECIMALS;

/**
 * Round a money total onto two decimal places. Symmetric about zero, same
 * reason `roundQuantity` is — undo of a priced deduct must land exactly.
 */
export function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return value;
  const rounded = Math.round(Math.abs(value) * MONEY_FACTOR) / MONEY_FACTOR;
  return value < 0 ? -rounded : rounded;
}

/** Whether any lot in the cupboard carries a price. */
export function cupboardHasPrices(inventory: InventoryTable): boolean {
  return Object.values(inventory).some((lot) => lot.unitCost !== null);
}

/**
 * Replacement unit cost for a shopping-list / shortfall line: the most
 * recently added *priced* lot that {@link lotMatches} calls the same
 * ingredient, or `null` when no such lot carries a price.
 *
 * Last paid, not FIFO remaining — a shopping trip is "what will this cost
 * at the shop", which is what you last paid for that ingredient.
 *
 * The search deliberately runs the **matcher**, not a key comparison. Keying
 * both sides is tempting and wrong: a recipe row keys on its link from the
 * moment the ingredient is picked out of the library, while the sack beside it
 * on the shelf keys on its name until it is stocked against that ingredient or
 * backfilled. The allocator matches those two on the name and spends the
 * stock; if pricing disagreed, the brewer would watch a replacement price they
 * saw yesterday disappear from an estimate that is still allocating the very
 * lot it came from. Reach is not preference — once several lots match, the
 * newest priced one wins, as it always did.
 *
 * `ingredientId` is the caller's already-resolved link, and `customIngredients`
 * is what resolves the *lot's*. Both sides have to be read the same way or the
 * divergence this function exists to prevent simply reappears mirrored. Pass
 * the row's id resolved and the lot's at face value and a lot whose link points
 * at a deleted entry keeps that dead id here while the allocator — which is
 * handed the library — reads it as absent and falls through to the name. The
 * allocator then spends that very lot on the row, and pricing says it is a
 * different ingredient and returns `null`: the brewer watches the price they
 * paid yesterday vanish from an estimate that is still drawing on the sack it
 * came from. So the table is threaded in, and a caller with no library to hand
 * may still pass `null` — which takes every id at face value on both sides, and
 * is consistent, if occasionally stricter than the resolved answer.
 */
export function replacementUnitCost(
  kind: InventoryKind,
  ingredientId: string | null,
  catalogRef: CatalogRef | null,
  name: string,
  inventory: InventoryTable,
  customIngredients: CustomIngredientTable | null = null,
): number | null {
  const wanted: IngredientIdentity = { kind, ingredientId, catalogRef, name };
  let best: { createdAt: number; id: string; unitCost: number } | null = null;
  for (const lot of Object.values(inventory)) {
    if (lot.unitCost === null) continue;
    if (!lotMatches(lot, wanted, customIngredients)) continue;
    if (
      best === null ||
      lot.createdAt > best.createdAt ||
      (lot.createdAt === best.createdAt && lot.id.localeCompare(best.id) > 0)
    ) {
      best = { createdAt: lot.createdAt, id: lot.id, unitCost: lot.unitCost };
    }
  }
  return best?.unitCost ?? null;
}

function lineCostOf(quantity: number, unitCost: number | null): number | null {
  if (unitCost === null) return null;
  return roundMoney(quantity * unitCost);
}

/** One line of a live recipe cost estimate. */
export interface RecipeCostLine {
  /** The lot this came from, or `null` for a shortfall priced at replacement. */
  readonly inventoryItemId: string | null;
  readonly name: string;
  readonly kind: InventoryKind;
  readonly quantity: number;
  readonly unit: InventoryUnit;
  readonly unitCost: number | null;
  readonly lineCost: number | null;
  readonly source: 'stock' | 'shortfall';
}

/** What {@link estimateRecipeCost} produces. */
export interface RecipeCostEstimate {
  readonly currency: string;
  readonly lines: readonly RecipeCostLine[];
  /** Sum of priced lines. Unpriced lines contribute nothing. */
  readonly total: number;
  /** True when any line has no unit cost. */
  readonly unpriced: boolean;
}

function summarise(currency: string, lines: readonly RecipeCostLine[]): RecipeCostEstimate {
  const total = roundMoney(lines.reduce((sum, line) => sum + (line.lineCost ?? 0), 0));
  return {
    currency,
    lines,
    total,
    unpriced: lines.some((line) => line.unitCost === null),
  };
}

/**
 * Live estimate of what brewing `recipe` would cost from the current cupboard.
 *
 * Allocated slices use each lot's own unit cost (FIFO, same as deduct).
 * Shortfalls use {@link replacementUnitCost}. Fully stocked, this equals
 * {@link costFromDeductionPlan} on the same inputs.
 */
export function estimateRecipeCost(
  recipe: RecipeDoc,
  inventory: InventoryTable,
  currency: string,
  customIngredients: CustomIngredientTable | null = null,
): RecipeCostEstimate {
  const allocated = allocateRequirements(
    recipeRequirements(recipe, customIngredients),
    inventory,
    customIngredients,
  );
  const byLot = new Map<string, RecipeCostLine>();
  const shortfalls: RecipeCostLine[] = [];

  for (const line of allocated) {
    for (const allocation of line.allocations) {
      const lot = inventory[allocation.inventoryItemId];
      if (lot === undefined) continue;
      const existing = byLot.get(lot.id);
      const quantity = roundQuantity((existing?.quantity ?? 0) + allocation.quantity);
      const unitCost = lot.unitCost;
      byLot.set(lot.id, {
        inventoryItemId: lot.id,
        name: lot.name,
        kind: lot.kind,
        quantity,
        unit: line.unit,
        unitCost,
        lineCost: lineCostOf(quantity, unitCost),
        source: 'stock',
      });
    }
    if (line.shortfall > 0) {
      const unitCost = replacementUnitCost(
        line.kind,
        line.ingredientId,
        line.catalogRef,
        line.name,
        inventory,
        customIngredients,
      );
      shortfalls.push({
        inventoryItemId: null,
        name: line.name,
        kind: line.kind,
        quantity: line.shortfall,
        unit: line.unit,
        unitCost,
        lineCost: lineCostOf(line.shortfall, unitCost),
        source: 'shortfall',
      });
    }
  }

  const lines = [...byLot.values(), ...shortfalls].filter((line) => line.quantity > 0);
  return summarise(currency, lines);
}

/**
 * Frozen COGS from a deduction plan. Consumed lots only — shortfalls make
 * `unpriced` true (the cupboard did not cover the recipe) but do not invent
 * a purchase that never went through inventory.
 */
export function costFromDeductionPlan(
  plan: InventoryDeductionPlan,
  inventory: InventoryTable,
  currency: string,
): BatchCostSnapshot {
  const lines: BatchCostLine[] = plan.lines.map((line) => {
    const lot = inventory[line.inventoryItemId];
    const unitCost = lot?.unitCost ?? null;
    return {
      inventoryItemId: line.inventoryItemId,
      ingredientId: lot?.ingredientId ?? null,
      name: line.name,
      kind: line.kind,
      quantity: line.quantity,
      unit: line.unit,
      unitCost,
      lineCost: lineCostOf(line.quantity, unitCost),
    };
  });

  const total = roundMoney(lines.reduce((sum, line) => sum + (line.lineCost ?? 0), 0));
  const consumedUnpriced = lines.some((line) => line.unitCost === null);
  return {
    currency,
    lines,
    total,
    unpriced: consumedUnpriced || plan.shortfalls.length > 0,
  };
}

/** One shopping-list shortfall, priced at replacement cost when known. */
export interface ShoppingCostLine {
  readonly key: string;
  readonly unitCost: number | null;
  readonly lineCost: number | null;
}

/** What {@link estimateShoppingListCost} produces. */
export interface ShoppingListCost {
  readonly currency: string;
  readonly lines: readonly ShoppingCostLine[];
  readonly total: number;
  readonly unpriced: boolean;
}

/**
 * Estimated cost of buying everything on a shopping list, using each
 * ingredient's most recently added priced lot as replacement cost.
 *
 * @param customIngredients - The workspace's library, so a lot whose link has
 *   died reads as unlinked and can still be found by name — the same resolution
 *   `shoppingList` itself used to work out the shortfalls being priced here.
 *   `null` takes every id at face value, which is only ever consistent, never
 *   more generous than the list it prices.
 */
export function estimateShoppingListCost(
  list: ShoppingList,
  inventory: InventoryTable,
  currency: string,
  customIngredients: CustomIngredientTable | null = null,
): ShoppingListCost {
  const lines = list.lines.map((line) => {
    const unitCost = replacementUnitCost(
      line.kind,
      line.ingredientId,
      line.catalogRef,
      line.name,
      inventory,
      customIngredients,
    );
    return {
      key: line.key,
      unitCost,
      lineCost: lineCostOf(line.shortfall, unitCost),
    };
  });
  return {
    currency,
    lines,
    total: roundMoney(lines.reduce((sum, line) => sum + (line.lineCost ?? 0), 0)),
    unpriced: lines.some((line) => line.unitCost === null),
  };
}
